import { DataList as ChakraDataList } from "@chakra-ui/react";
import * as React from "react";
import { InfoTip } from "./toggle-tip";

export const DataListRoot = ChakraDataList.Root;

interface ItemProps extends ChakraDataList.ItemProps {
  label: React.ReactNode;
  value: React.ReactNode;
  info?: React.ReactNode;
  grow?: boolean;
  labelProps?: any;
  valueProps?: any;
}

export const DataListItem = React.forwardRef<HTMLDivElement, ItemProps>(
  function DataListItem(props, ref) {
    const {
      label,
      info,
      value,
      children,
      grow,
      labelProps,
      valueProps,
      ...rest
    } = props;
    return (
      <ChakraDataList.Item ref={ref} {...rest}>
        <ChakraDataList.ItemLabel
          flex={grow ? "1" : undefined}
          color="tertiary.fg"
          textStyle="fieldLabel"
          {...labelProps}
        >
          {label}
          {info && <InfoTip>{info}</InfoTip>}
        </ChakraDataList.ItemLabel>
        <ChakraDataList.ItemValue
          flex={1}
          width="100%"
          justifyContent="flex-end"
          alignItems="center"
          textStyle="fieldContent"
          {...valueProps}
        >
          {value}
        </ChakraDataList.ItemValue>
        {children}
      </ChakraDataList.Item>
    );
  }
);

interface DataListProps extends Omit<ChakraDataList.RootProps, "children"> {
  items: ItemProps[];
  orientation?: "horizontal" | "vertical";
  divideY?: string;
  divideX?: string;
  variant?: "subtle" | "bold";
  children?: React.ReactNode;
  labelProps?: any;
  valueProps?: any;
}

export const DataList = React.forwardRef<HTMLDivElement, DataListProps>(
  function DataList(props, ref) {
    const {
      items,
      orientation = "horizontal",
      divideX,
      divideY,
      children,
      variant,
      labelProps,
      valueProps,
      ...rest
    } = props;
    return (
      <ChakraDataList.Root
        {...rest}
        variant={variant}
        orientation={orientation}
        divideY={divideY}
        divideX={divideX}
        display="flex"
        flex={1}
        height="100%"
        width="100%"
        overflow="scroll"
        gap={2}
      >
        {items.map((item, index) => (
          <DataListItem
            {...item}
            key={`data-list-item-${index}`}
            label={item.label}
            value={item.value}
            info={item.info}
            grow={item.grow}
            pt={2}
            px={2}
            width="100%"
            labelProps={item.labelProps || labelProps}
            valueProps={item.valueProps || valueProps}
          >
            {children}
          </DataListItem>
        ))}
      </ChakraDataList.Root>
    );
  }
);
